import { useMemo, useCallback } from 'react';
import { set, getHours, getMinutes, addHours, format } from 'date-fns';
import { HourRange } from '@/types';

const MINUTES_PER_HOUR = 60;

interface UseTimeCalculationsProps {
  displayStartTime?: Date;
  displayEndTime?: Date;
  openingHours: HourRange[];
}

interface DayDisplayTimes {
  startTime: Date;
  endTime: Date;
  totalHours: number;
}

export const useTimeCalculations = ({
  displayStartTime,
  displayEndTime,
  openingHours
}: UseTimeCalculationsProps) => {
  // Auto-calculate display time range when not provided
  const autoCalculatedTimes = useMemo(() => {
    if (displayStartTime && displayEndTime) {
      return { startTime: displayStartTime, endTime: displayEndTime };
    }
    
    // Auto-calculate based on opening hours - find the longest open day
    if (openingHours.length > 0) {
      let longestDayDuration = 0;
      let longestDayStart: Date | null = null;
      let longestDayEnd: Date | null = null;
      let hasAny24HourDay = false;
      
      // Check if ALL days are closed first
      const allDaysClosed = openingHours.every(oh => oh.isClosedAllDay);

      if (allDaysClosed) {
        // ALL days are closed - show 24 hours
        const baseDate = new Date();
        const result = {
          startTime: set(baseDate, { hours: 0, minutes: 0, seconds: 0, milliseconds: 0 }),
          endTime: set(baseDate, { hours: 23, minutes: 59, seconds: 59, milliseconds: 999 })
        };
        return result;
      }

      // Process only open days to find the longest one
      openingHours.forEach(oh => {
        if (oh.isClosedAllDay) return; // Skip closed days entirely
        
        if (oh.is24Hours) {
          hasAny24HourDay = true;
          return; // 24-hour days will be handled separately
        }

        // Calculate duration for this day
        const startMinutes = getHours(oh.startTime) * 60 + getMinutes(oh.startTime);
        const endMinutes = getHours(oh.endTime) * 60 + getMinutes(oh.endTime);
        
        let duration;
        if (endMinutes < startMinutes) {
          // Overnight operation: add 24 hours to end time
          duration = (endMinutes + 24 * 60) - startMinutes;
        } else {
          // Same-day operation
          duration = endMinutes - startMinutes;
        }
        
        if (duration > longestDayDuration) {
          longestDayDuration = duration;
          longestDayStart = oh.startTime;
          longestDayEnd = oh.endTime;
        }
      });

      // Determine final time range
      const baseDate = new Date();

      if (hasAny24HourDay) {
        // Use 24-hour range for 24-hour businesses - but only if there are actually 24h days that are OPEN
        const result = {
          startTime: set(baseDate, { hours: 0, minutes: 0, seconds: 0, milliseconds: 0 }),
          endTime: set(baseDate, { hours: 23, minutes: 59, seconds: 59, milliseconds: 999 })
        };
        return result;
      } else if (longestDayStart && longestDayEnd) {
        // Use the longest day's exact times (no buffer)
        return {
          startTime: set(baseDate, { 
            hours: getHours(longestDayStart), 
            minutes: getMinutes(longestDayStart),
            seconds: 0,
            milliseconds: 0
          }),
          endTime: set(baseDate, { 
            hours: getHours(longestDayEnd), 
            minutes: getMinutes(longestDayEnd),
            seconds: 59,
            milliseconds: 999
          })
        };
      }
    }

    // Default fallback (24 hours)
    const baseDate = new Date();
    return {
      startTime: set(baseDate, { hours: 0, minutes: 0, seconds: 0, milliseconds: 0 }),
      endTime: set(baseDate, { hours: 23, minutes: 59, seconds: 59, milliseconds: 999 })
    };
  }, [displayStartTime, displayEndTime, openingHours]);

  const effectiveDisplayStartTime = autoCalculatedTimes.startTime;
  const effectiveDisplayEndTime = autoCalculatedTimes.endTime;

  // Calculate the total number of hours to display
  const totalDisplayHours = useMemo(() => {
    // Calculate the difference in minutes between the effective display times
    const diffMinutes = Math.abs(effectiveDisplayEndTime.getTime() - effectiveDisplayStartTime.getTime()) / (1000 * 60);

    // Convert minutes to hours
    const calculatedHours = diffMinutes / MINUTES_PER_HOUR;
    
    // The autoCalculatedTimes logic already handles all edge cases,
    // so we just need to return the calculated hours
    return calculatedHours;
  }, [effectiveDisplayStartTime, effectiveDisplayEndTime]);

  // Function to calculate display times for individual days
  const getDisplayTimesForDay = useCallback((dayDate: Date, openingHoursByDayOfWeek: Map<string, HourRange>): DayDisplayTimes => {
    const dayOfWeek = format(dayDate, 'EEEE');
    const openingHoursForDay = openingHoursByDayOfWeek.get(dayOfWeek);
    
    // For all days (open and closed), use the global display times
    // This ensures visual consistency across all days in the calendar
    // Closed days will still show the "CLOSED" overlay to indicate their status
    return {
      startTime: effectiveDisplayStartTime,
      endTime: effectiveDisplayEndTime,
      totalHours: totalDisplayHours
    };
  }, [effectiveDisplayStartTime, effectiveDisplayEndTime, totalDisplayHours]);

  return {
    effectiveDisplayStartTime,
    effectiveDisplayEndTime,
    totalDisplayHours,
    getDisplayTimesForDay
  };
};